/* @flow */

import type { BasicFunction, KeyValuePair } from '../../helpers/jsHelpers/types';
import type { ApplicationInfo } from '../../redux/netgemApi/actions/emitter';
import type { BO_RESPONSE_WITH_ETAG } from '../../redux/netgemApi/actions/videofutur/types/common';
import type { CombinedReducers } from '../../redux/reducers';
import type { NETGEM_API_DMS_ASSOCIATED_DEVICE } from './dms/types/AssociatedDevice';
import type { NETGEM_API_DMS_CHANNEL_LIST } from './dms/types/ChannelList';
import type { DmsSettingMap } from './dms/types/DeviceInfoSettings';
import type { NETGEM_API_DMS_QUOTA_PVR } from './dms/types/QuotaPvr';
import type { NETGEM_API_DMS_REQUEST_RESPONSE_ASSOCIATED_DEVICE } from './dms/types/RequestResponseAssociatedDevice';
import type { NETGEM_API_DMS_REQUEST_RESPONSE_CHALLENGE } from './dms/types/RequestResponseChallenge';
import type { NETGEM_API_DMS_REQUEST_RESPONSE_QUOTA_PVR } from './dms/types/RequestResponseQuotaPvr';
import type { NETGEM_API_ENTITLEMENT_RESULT } from './ntgEntitlement/types';
import type { ChannelMap } from './v8/types/Channel';
import type { NETGEM_API_V8_DATA_COLLECTION_BATCH_PAYLOAD } from './v8/types/DataCollection';
import type { NETGEM_API_V8_FEED } from './v8/types/FeedItem';
import type { NETGEM_API_V8_REQUEST_HEADERS } from './v8/types/Headers';
import type { ScheduledRecordingsKindType } from './v8/types/Npvr';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from './v8/types/RequestResponse';
import type { NETGEM_API_V8_SECTION } from './v8/types/Section';
import type { BO_API_AUTHENTICATION_SETTINGS } from './videofutur/types/AuthenticationSettings';

import AccurateTimestamp from '../../helpers/dateTime/AccurateTimestamp';
import { MILLISECONDS_PER_SECOND } from '../../helpers/dateTime/Format';
import { logDebug, logError, logInfo, logWarning } from '../../helpers/debug/debug';
import { areSettingsEqual } from '../../helpers/dms/helper';
import { StorageKeys } from '../../helpers/localStorage/keys';
import LocalStorageManager from '../../helpers/localStorage/localStorageManager';
import { deleteOptimisticData } from '../../helpers/localStorage/optimisticData';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import { getVfAuthenticationSettings } from '../../helpers/videofutur/authenticationSettings';
import { sendV8DataCollectionCollectorIdRequest, sendV8DataCollectionRequest } from './dataCollection/requests';
import { DmsNetworkCode, getMessageFromErrorCode } from './dms/constants/NetworkCodesAndMessages';
import dmsAssociatedDeviceRequest from './dms/requests/associatedDevices';
import dmsDeviceChannelsRequest from './dms/requests/deviceChannels';
import dmsDeviceSettingsRequest from './dms/requests/deviceSettings';
import dmsLoginRequest from './dms/requests/login';
import dmsLogoutRequest from './dms/requests/logout';
import dmsQuotaPvrRequest from './dms/requests/quotaPvr';
import { createCustomNetworkError } from './helpers/CreateCustomNetworkError';
import { CustomNetworkError } from './helpers/CustomNetworkError';
import sendNtgEntitlementRequest from './ntgEntitlement/requests';
import sendPersonalDataGetRequest from './personalData/requests/get';
import sendPersonalDataPostRequest from './personalData/requests/post';
import { sendV8SectionFeedRequest } from './v8/requests/feed';
import { sendV8GenericRequest } from './v8/requests/generic';
import sendV8ListAliasPostRequest from './v8/requests/listAlias';
import { sendV8ScheduledRecordingCreateRequest, sendV8ScheduledRecordingUpdateRequest } from './v8/requests/scheduledRecordings';
import { HttpMethod } from './v8/types/HttpMethod';
import boAuthenticationRequest from './videofutur/requests/authentication';
import { sendBORequest } from './videofutur/requests/bo';
import boDiscoveryRequest from './videofutur/requests/discovery';

enum TokenRenewalReason {
  Forced,
  Timer,
  VisibilityOrFocusChange,
}

const TWO = 2;

/*
 * Every 5 seconds, next renewal date is checked against current date, for DMS and BO tokens
 * NOTE: setTimeout is not used because it does not work properly when computer goes to sleep
 */
const TOKEN_RENEWAL_CHECK_INTERVAL = 5_000;

// Maximum number of retries for DMS authentication token
const DMS_TOKEN_RENEWAL_MAX_RETRIES = 6;

// Base backoff delay for DMS authentication token (in ms)
const DMS_TOKEN_RENEWAL_BASE_BACKOFF = 5_000;

// DMS authentication token is renewed 5 minutes before its expiration date (in ms)
const DMS_TOKEN_RENEWAL_EXPIRATION_MARGIN = 300_000;

// Maximum number of retries for BO authentication token
const BO_TOKEN_RENEWAL_MAX_RETRIES = 6;

// Base backoff delay for BO authentication token (in ms)
const BO_TOKEN_RENEWAL_BASE_BACKOFF = 5_000;

// Minimum number of DMS settings for a response to be considered valid
const DMS_SETTINGS_MIN_SIZE = 15;

// Delay before actually doing DMS settings query when a previous one has been found in the local storage (in ms)
const OPTIMISTIC_DMS_SETTINGS_REFRESH_DELAY = 10;

type DmsAuthenticationTokenDataType = {
  applicationId: string,
  authDeviceUrl: string,
  deviceKey: string,
  oem: ?string,
  subscriberId: string,
  version: ?string,
};

type BOAuthenticationTokenDataType = {
  appKey: string,
  applicationId: string,
  authenticationUrl: string,
  deviceKey: string,
};

type ConnectedCallbackType = (authenticationToken: string | null) => void;
type DisconnectedCallbackType = BasicFunction;
type DeviceSettingsCallbackType = (deviceSettings: DmsSettingMap) => void;
type DeviceChannelsCallbackType = (deviceChannels: ChannelMap) => void;
type AssociatedDevicesCallbackType = (associatedDevices: Array<NETGEM_API_DMS_ASSOCIATED_DEVICE>) => void;
type QuotaPvrCallbackType = (quotaPvr: ?NETGEM_API_DMS_QUOTA_PVR) => void;
type BOAuthenticationCallbackType = (settings?: KeyValuePair<string>) => void;

class NetgemApi {
  appInfo: ApplicationInfo | null;

  associatedDevicesCallback: AssociatedDevicesCallbackType;

  authenticationToken: string | null;

  baseUpgradeDeviceUrl: string;

  boAuthenticationCallback: ?BOAuthenticationCallbackType;

  boTokenData: BOAuthenticationTokenDataType | null;

  boTokenRetryCount: number;

  boTokenRenewalTimer: IntervalID | null;

  connectedCallback: ConnectedCallbackType;

  deviceChannelsCallback: DeviceChannelsCallbackType;

  deviceSettingsCallback: DeviceSettingsCallbackType;

  disconnectedCallback: DisconnectedCallbackType;

  dmsTokenData: DmsAuthenticationTokenDataType | null;

  dmsTokenRenewalTimer: IntervalID | null;

  dmsTokenRetryCount: number;

  isDmsTokenRenewing: boolean;

  nextBOTokenRenewalDate: number;

  nextDmsTokenRenewalDate: number;

  quotaPvrCallback: ?QuotaPvrCallbackType;

  constructor(
    connectedCallback: ConnectedCallbackType,
    disconnectedCallback: DisconnectedCallbackType,
    deviceSettingsCallback: DeviceSettingsCallbackType,
    deviceChannelsCallback: DeviceChannelsCallbackType,
    associatedDevicesCallback: AssociatedDevicesCallbackType,
    quotaPvrCallback: ?QuotaPvrCallbackType,
    boAuthenticationCallback: ?BOAuthenticationCallbackType,
  ) {
    this.associatedDevicesCallback = associatedDevicesCallback;
    this.appInfo = null;
    this.authenticationToken = null;
    this.boTokenData = null;
    this.baseUpgradeDeviceUrl = '';
    this.boAuthenticationCallback = boAuthenticationCallback;
    this.boTokenRenewalTimer = null;
    this.boTokenRetryCount = 0;
    this.connectedCallback = connectedCallback;
    this.deviceChannelsCallback = deviceChannelsCallback;
    this.deviceSettingsCallback = deviceSettingsCallback;
    this.disconnectedCallback = disconnectedCallback;
    this.dmsTokenData = null;
    this.dmsTokenRetryCount = 0;
    this.dmsTokenRenewalTimer = null;
    this.isDmsTokenRenewing = false;
    this.nextBOTokenRenewalDate = 0;
    this.nextDmsTokenRenewalDate = 0;
    this.quotaPvrCallback = quotaPvrCallback;

    Messenger.on(MessengerEvents.REFRESH_AUTHENTICATION_TOKEN, this.refreshDmsToken);
    Messenger.on(MessengerEvents.DMS_REQUEST, this.refreshDmsSettings);

    // Register visibility and focus listeners for tokens renewal
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleFocus);
  }

  // $FlowFixMe[unsupported-syntax] Flow does not support symbols yet
  get [Symbol.toStringTag]() {
    return 'NetgemApi';
  }

  reset: () => void = () => {
    this.resetTokenRenewalTimer();
    this.resetBOTokenRenewalTimer();
    this.authenticationToken = null;
    this.disconnectedCallback();
  };

  handleVisibilityChange: () => void = () => {
    if (document.visibilityState === 'visible') {
      this.checkAndRenewTokensIfNeeded();
    }
  };

  handleFocus: () => void = () => {
    this.checkAndRenewTokensIfNeeded();
  };

  checkErrorMessage: (error: CustomNetworkError) => void = (error) => {
    try {
      const customStatus = error.getCustomStatus();
      // Missing or invalid authentication token
      if (
        customStatus === (DmsNetworkCode.MissingOrInvalidAuthenticationToken as number) ||
        // Unknown device
        customStatus === (DmsNetworkCode.UnknownDevice as number) ||
        // Invalid device
        customStatus === (DmsNetworkCode.InvalidDevice as number) ||
        // DMS did not provide correct error code
        customStatus === (DmsNetworkCode.UnknownError as number)
      ) {
        // The reasons above trigger a reset
        this.reset();
      }
      // For all other statuses, execution proceeds
    } catch {
      // Not being able to get status also triggers a reset
      this.reset();
    }
  };

  refreshDmsToken: (reason: TokenRenewalReason) => void = (reason = TokenRenewalReason.Forced) => {
    if (!this.dmsTokenData) {
      logError('Cannot refresh DMS authentication token because of missing data');
      return;
    }

    const {
      dmsTokenData: { applicationId, authDeviceUrl, deviceKey, oem, subscriberId, version },
      isDmsTokenRenewing,
    } = this;

    if (isDmsTokenRenewing) {
      return;
    }

    this.resetTokenRenewalTimer();
    this.sendLoginRequest(authDeviceUrl, null, applicationId, subscriberId, deviceKey, oem, version).catch((error) => {
      logError(`Error refreshing DMS authentication token (${reason as string}): ${error.message}`);
      logError(error instanceof CustomNetworkError ? error.networkError : error);

      if (this.dmsTokenRetryCount >= DMS_TOKEN_RENEWAL_MAX_RETRIES) {
        // Max number of retries has been reached: reset app
        logWarning('Max retries reached for DMS token. Giving up.');
        this.reset();
        return;
      }

      // Exponential backoff: 5s, 10s, 20s, 40s, 80s, 160s
      const backoffDelay = DMS_TOKEN_RENEWAL_BASE_BACKOFF * Math.pow(TWO, this.dmsTokenRetryCount);
      this.nextDmsTokenRenewalDate = AccurateTimestamp.now() + backoffDelay;

      this.dmsTokenRetryCount += 1;
      const retriesLeft = DMS_TOKEN_RENEWAL_MAX_RETRIES - this.dmsTokenRetryCount;
      logInfo(`Will retry DMS token renewal in ${backoffDelay / MILLISECONDS_PER_SECOND}s (${retriesLeft} retry left)...`);
    });
  };

  refreshBOToken: (reason: TokenRenewalReason) => void = (reason = TokenRenewalReason.Forced) => {
    if (!this.boTokenData) {
      logError('Cannot refresh BO authentication token because of missing data');
      return;
    }

    const {
      boTokenData: { appKey, applicationId, authenticationUrl, deviceKey },
    } = this;

    this.sendBOAuthenticationRequest(authenticationUrl, applicationId, deviceKey, appKey)
      .then(() => {
        // Reset retry counter on success
        this.boTokenRetryCount = 0;
      })
      .catch((error) => {
        logError(`Error refreshing BO authentication token (${reason as string}): ${error.message}`);

        if (this.boTokenRetryCount >= BO_TOKEN_RENEWAL_MAX_RETRIES) {
          logWarning('Max BO token renewal retries reached. Stopping BO renewal timer.');
          this.resetBOTokenRenewalTimer();
          if (this.boAuthenticationCallback) {
            this.boAuthenticationCallback(undefined);
          }
          return;
        }

        // Exponential backoff: 5s, 10s, 20s, 40s, 80s, 160s
        const backoffDelay = BO_TOKEN_RENEWAL_BASE_BACKOFF * Math.pow(TWO, this.boTokenRetryCount);
        this.nextBOTokenRenewalDate = AccurateTimestamp.now() + backoffDelay;

        this.boTokenRetryCount += 1;
        const retriesLeft = BO_TOKEN_RENEWAL_MAX_RETRIES - this.boTokenRetryCount;
        logInfo(`Will retry BO token renewal in ${backoffDelay / MILLISECONDS_PER_SECOND}s (${retriesLeft} retry left)...`);
      });
  };

  checkAndRenewTokensIfNeeded: () => void = () => {
    const now = AccurateTimestamp.now();

    // Check DMS Token
    if (this.nextDmsTokenRenewalDate > 0 && now >= this.nextDmsTokenRenewalDate && !this.isDmsTokenRenewing) {
      this.refreshDmsToken(TokenRenewalReason.VisibilityOrFocusChange);
    }

    // Check BO Token
    if (this.nextBOTokenRenewalDate > 0 && now >= this.nextBOTokenRenewalDate) {
      this.refreshBOToken(TokenRenewalReason.VisibilityOrFocusChange);
    }
  };

  startDmsTokenRenewalTimer: () => void = () => {
    if (this.dmsTokenRenewalTimer) {
      clearInterval(this.dmsTokenRenewalTimer);
    }

    this.dmsTokenRenewalTimer = setInterval(() => {
      const { nextDmsTokenRenewalDate } = this;

      if (nextDmsTokenRenewalDate > 0 && AccurateTimestamp.now() >= nextDmsTokenRenewalDate) {
        this.refreshDmsToken(TokenRenewalReason.Timer);
      }
    }, TOKEN_RENEWAL_CHECK_INTERVAL);
  };

  startBOTokenRenewalTimer: () => void = () => {
    if (this.boTokenRenewalTimer) {
      // Do not start timer more than once
      return;
    }

    this.boTokenRenewalTimer = setInterval(() => {
      const { nextBOTokenRenewalDate } = this;

      if (nextBOTokenRenewalDate > 0 && AccurateTimestamp.now() >= nextBOTokenRenewalDate) {
        this.refreshBOToken(TokenRenewalReason.Timer);
      }
    }, TOKEN_RENEWAL_CHECK_INTERVAL);
  };

  resetTokenRenewalTimer: () => void = () => {
    if (this.dmsTokenRenewalTimer) {
      clearInterval(this.dmsTokenRenewalTimer);
      this.dmsTokenRenewalTimer = null;
    }
    this.nextDmsTokenRenewalDate = 0;
    this.dmsTokenRetryCount = 0;
  };

  resetBOTokenRenewalTimer: () => void = () => {
    if (this.boTokenRenewalTimer) {
      clearInterval(this.boTokenRenewalTimer);
      this.boTokenRenewalTimer = null;
    }
    this.nextBOTokenRenewalDate = 0;
    this.boTokenRetryCount = 0;
  };

  refreshDmsSettings: (fcmToken: string) => Promise<void> = async (fcmToken) => {
    const { appInfo } = this;

    if (appInfo === null) {
      // Should never happen since appInfo is set at startup
      logInfo('Cannot do DMS request because appInfo is null');
      return;
    }

    logInfo('DMS request...');
    await this.getDeviceSettings({ ...appInfo, fcmToken }, undefined, true);
    logInfo('DMS request ✅');
  };

  dmsSettingsSuccessCallback: (settings: DmsSettingMap, isChecking?: boolean, optimisticDmsSettings: DmsSettingMap | null) => void = (settings, isChecking, optimisticDmsSettings) => {
    const optimisticDmsSettingsFound = optimisticDmsSettings !== null;

    if (isChecking && optimisticDmsSettingsFound && areSettingsEqual(settings, optimisticDmsSettings)) {
      // Stored settings are still up to date
      return;
    }

    const settingsCount = Object.keys(settings).length;

    if (settingsCount === 0) {
      // DMS settings empty
      deleteOptimisticData();
      if (isChecking) {
        // When checking DMS in background, promise would remain uncaught
        return;
      }
      throw new CustomNetworkError({
        message: 'Empty DMS settings',
        status: -1,
      });
    }

    if (settingsCount <= DMS_SETTINGS_MIN_SIZE) {
      // DMS settings seem incomplete
      deleteOptimisticData();
      if (isChecking) {
        // When checking DMS in background, promise would remain uncaught
        return;
      }

      throw new CustomNetworkError({
        message: 'Incomplete DMS settings',
        status: -1,
      });
    }

    // Parse and store settings (
    this.deviceSettingsCallback(settings);

    const optimisticDmsSettingsEnabled = LocalStorageManager.loadBoolean(StorageKeys.OptimisticDmsSettingsEnabled, true);
    if (optimisticDmsSettingsEnabled) {
      // Store settings in local storage for future optimistic use
      LocalStorageManager.save(StorageKeys.OptimisticDmsSettings, settings);
    }

    if (!isChecking) {
      // Settings retrieved from backend, used and stored, and no optimistic settings were found
      return;
    }

    // Newly retrieved settings are different from the cached ones: reload app
    Messenger.emit(MessengerEvents.RESTART_APP);
  };

  getDeviceSettings: (appInfo: ApplicationInfo, signal?: AbortSignal, isChecking?: boolean) => Promise<void> = (appInfo, signal, isChecking) => {
    // Store appInfo for future uses
    if (this.appInfo === null) {
      this.appInfo = appInfo;
    }

    const optimisticDmsSettingsEnabled = LocalStorageManager.loadBoolean(StorageKeys.OptimisticDmsSettingsEnabled, true);
    const optimisticDmsSettings = LocalStorageManager.loadObject(StorageKeys.OptimisticDmsSettings);

    // 1. On initial request, isChecking is false, ensuring cached DMS settings are checked
    if (!isChecking && optimisticDmsSettingsEnabled) {
      if (optimisticDmsSettings && Object.keys(optimisticDmsSettings).length > DMS_SETTINGS_MIN_SIZE) {
        // Startup optimization: get last DMS settings from local storage and do the actual query in background after a short delay
        this.deviceSettingsCallback(optimisticDmsSettings);

        // Schedule background verification
        setTimeout(() => {
          // No cancel token because checking DMS settings should not be canceled
          this.getDeviceSettings(appInfo, undefined, true).catch((error) => logError(`Error retrieving DMS settings: ${error.message}`));
        }, OPTIMISTIC_DMS_SETTINGS_REFRESH_DELAY);

        return Promise.resolve();
      }
    }

    // 2. Fetch fresh settings (either because no cache DMS settings were found, or this is the background check)
    return dmsDeviceSettingsRequest(this.baseUpgradeDeviceUrl, this.authenticationToken, appInfo, signal)
      .then((settings) => {
        signal?.throwIfAborted();
        this.dmsSettingsSuccessCallback(settings, isChecking, optimisticDmsSettings);
      })
      .catch((error: CustomNetworkError) => {
        this.checkErrorMessage(error);
        if (isChecking) {
          // Settings have been retrieved in background and promise rejection would not be caught
          return;
        }

        throw error;
      });
  };

  getDeviceChannelList: (signal?: AbortSignal) => Promise<void> = (signal) =>
    dmsDeviceChannelsRequest(this.baseUpgradeDeviceUrl, this.authenticationToken, signal)
      .then((param: { deviceChannelNetworkResponse: NETGEM_API_DMS_CHANNEL_LIST, deviceChannels: ChannelMap }) => {
        signal?.throwIfAborted();

        this.deviceChannelsCallback(param.deviceChannels);
      })
      .catch((error: CustomNetworkError) => {
        this.checkErrorMessage(error);
        throw error;
      });

  getAssociatedDeviceList: (signal?: AbortSignal) => Promise<NETGEM_API_DMS_REQUEST_RESPONSE_ASSOCIATED_DEVICE> = (signal) => {
    this.associatedDevicesCallback([]);

    return dmsAssociatedDeviceRequest(this.baseUpgradeDeviceUrl, this.authenticationToken, signal)
      .then((associatedDeviceNetworkResponse: NETGEM_API_DMS_REQUEST_RESPONSE_ASSOCIATED_DEVICE) => {
        signal?.throwIfAborted();

        this.associatedDevicesCallback(associatedDeviceNetworkResponse.list);
        return associatedDeviceNetworkResponse;
      })
      .catch((error: CustomNetworkError) => {
        this.checkErrorMessage(error);
        throw error;
      });
  };

  getQuotaPvr: () => Promise<NETGEM_API_DMS_REQUEST_RESPONSE_QUOTA_PVR> = () => {
    if (this.quotaPvrCallback) {
      this.quotaPvrCallback(null);
    }

    return dmsQuotaPvrRequest(this.baseUpgradeDeviceUrl, this.authenticationToken)
      .then((quotaPvrNetworkResponse: NETGEM_API_DMS_REQUEST_RESPONSE_QUOTA_PVR) => {
        if (this.quotaPvrCallback) {
          const { quotaPVR, quotaPVRUsed } = quotaPvrNetworkResponse;
          const quotaOnly: NETGEM_API_DMS_QUOTA_PVR = { quotaPVR, quotaPVRUsed };
          this.quotaPvrCallback(quotaOnly);
        }

        return quotaPvrNetworkResponse;
      })
      .catch((error: CustomNetworkError) => {
        this.checkErrorMessage(error);
        throw error;
      });
  };

  sendLogoutRequest: (logoutUrl?: string) => Promise<void> = (logoutUrl) =>
    dmsLogoutRequest(logoutUrl)
      .then(() => {
        this.reset();
      })
      .catch((error: CustomNetworkError) => {
        this.checkErrorMessage(error);
        throw error;
      });

  sendLoginRequest: (
    authDeviceUrl: string,
    upgradeDeviceUrl: ?string,
    applicationId: string,
    subscriberId: string,
    deviceKey: string,
    oem: ?string,
    version: ?string,
    isFirstToken: ?boolean,
    signal?: AbortSignal,
  ) => Promise<NETGEM_API_DMS_REQUEST_RESPONSE_CHALLENGE> = (authDeviceUrl, upgradeDeviceUrl, applicationId, subscriberId, deviceKey, oem, version, isFirstToken, signal) => {
    if (typeof upgradeDeviceUrl === 'string') {
      this.baseUpgradeDeviceUrl = upgradeDeviceUrl;
    }

    if (typeof applicationId !== 'string' || typeof subscriberId !== 'string' || typeof deviceKey !== 'string' || typeof authDeviceUrl !== 'string') {
      return Promise.reject(createCustomNetworkError(DmsNetworkCode.MissingOrInvalidRequiredParameters as number, getMessageFromErrorCode(DmsNetworkCode.MissingOrInvalidRequiredParameters)));
    }

    this.isDmsTokenRenewing = true;

    logDebug(`${typeof upgradeDeviceUrl === 'string' ? 'Requesting' : 'Renewing'} authentication token...`);

    return dmsLoginRequest(authDeviceUrl, applicationId, subscriberId, deviceKey, oem, version, signal)
      .then((jsonChallengeResponse: NETGEM_API_DMS_REQUEST_RESPONSE_CHALLENGE) => {
        const { expirationDate, token } = jsonChallengeResponse;

        this.isDmsTokenRenewing = false;

        logDebug(`Authentication token ${typeof upgradeDeviceUrl === 'string' ? 'received' : 'renewed'}`);

        // Reset retry count since the call succeeded
        this.dmsTokenRetryCount = 0;

        // Store token locally
        this.authenticationToken = token;

        // Store required data for future refresh
        this.dmsTokenData = { applicationId, authDeviceUrl, deviceKey, oem, subscriberId, version };

        // Store token in Redux store
        this.connectedCallback(token);

        this.nextDmsTokenRenewalDate = expirationDate - DMS_TOKEN_RENEWAL_EXPIRATION_MARGIN;

        // These 2 dates only exist for debug purpose and should be removed in a near future
        LocalStorageManager.save(StorageKeys.LastTokenRenewal, AccurateTimestamp.nowAsDate());
        LocalStorageManager.save(StorageKeys.NextTokenRenewal, new Date(this.nextDmsTokenRenewalDate));

        if (!this.dmsTokenRenewalTimer || isFirstToken) {
          // Do not start timer more than once but reset it if going from guest to authenticated (or the opposite)
          this.startDmsTokenRenewalTimer();
        }

        return jsonChallengeResponse;
      })
      .catch((error: CustomNetworkError) => {
        this.isDmsTokenRenewing = false;
        this.checkErrorMessage(error);
        throw error;
      });
  };

  sendBOAuthenticationRequest: (authenticationUrl: string, applicationId: string, deviceKey: string, appKey: string, signal?: AbortSignal) => Promise<KeyValuePair<string>> = (
    authenticationUrl,
    applicationId,
    deviceKey,
    appKey,
    signal,
  ) =>
    boAuthenticationRequest(authenticationUrl, applicationId, deviceKey, appKey, signal).then((authenticationSettings: BO_API_AUTHENTICATION_SETTINGS) => {
      const { authentication } = authenticationSettings;

      // Store required data for future refresh
      this.boTokenData = { appKey, applicationId, authenticationUrl, deviceKey };

      if (authentication) {
        const { identityExpiration } = authentication;

        if (identityExpiration) {
          this.nextBOTokenRenewalDate = identityExpiration - DMS_TOKEN_RENEWAL_EXPIRATION_MARGIN;

          // These 2 dates only exists for debug purpose and should be removed in a near future
          LocalStorageManager.save(StorageKeys.LastBOTokenRenewal, AccurateTimestamp.nowAsDate());
          LocalStorageManager.save(StorageKeys.NextBOTokenRenewal, new Date(this.nextBOTokenRenewalDate));

          this.startBOTokenRenewalTimer();
        }
      }

      const { error, settings } = getVfAuthenticationSettings(authenticationSettings);

      if (this.boAuthenticationCallback) {
        this.boAuthenticationCallback(settings);
      }

      if (settings && !error) {
        return settings;
      }

      throw error;
    });

  sendNtgEntitlementRequest: (
    url: string,
    applicationId: string,
    channelName: string,
    entId: string,
    entService: string,
    entUrl: string,
    method: HttpMethod,
    authenticationToken: string | null,
    signal?: AbortSignal,
  ) => Promise<NETGEM_API_ENTITLEMENT_RESULT | void> = (url, applicationId, channelName, entId, entService, entUrl, method, authenticationToken, signal) =>
    sendNtgEntitlementRequest(url, applicationId, channelName, entId, entService, entUrl, method, authenticationToken, signal);

  sendV8ListAliasPostRequest: (url: string, bodyParam: string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    bodyParam,
    method,
    authenticationToken,
    signal,
  ) => sendV8ListAliasPostRequest(url, bodyParam, method, authenticationToken, signal);

  sendV8GenericRequest: (
    url: string,
    method: HttpMethod,
    authenticationToken: string | null,
    responseType?: string,
    state?: CombinedReducers,
    signal?: AbortSignal,
  ) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (url, method, authenticationToken, responseType, state, signal) => sendV8GenericRequest(url, method, authenticationToken, state, signal, responseType);

  sendV8RecordingsDeleteRequest: (url: string, eTag: string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, '', eTag);

  sendV8RecordingsFutureRequest: (url: string, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, undefined, eTag);

  sendV8RecordingsListRequest: (url: string, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, undefined, eTag);

  sendV8RecordingsMetadataRequest: (url: string, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, undefined, eTag);

  sendV8RecordingsQuotaRequest: (url: string, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, undefined, eTag);

  sendV8RecordingsRetryRequest: (url: string, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, '', eTag);

  sendV8ScheduledRecordingCreateRequest: (
    url: string,
    scheduledRecordKind: ScheduledRecordingsKindType,
    target: string,
    fromBeginning: boolean,
    startMargin: number,
    endMargin: number,
    channelId: ?string,
    fromUtc: ?string,
    toUtc: ?string,
    increasingEpisodes: ?boolean,
    recordsToKeep: ?number,
    method: HttpMethod,
    authenticationToken: string | null,
    signal?: AbortSignal,
  ) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    scheduledRecordKind,
    target,
    fromBeginning,
    startMargin,
    endMargin,
    channelId,
    fromUtc,
    toUtc,
    increasingEpisodes,
    recordsToKeep,
    method,
    authenticationToken,
    signal,
  ) =>
    sendV8ScheduledRecordingCreateRequest(
      url,
      scheduledRecordKind,
      target,
      fromBeginning,
      startMargin,
      endMargin,
      channelId,
      fromUtc,
      toUtc,
      increasingEpisodes,
      recordsToKeep,
      method,
      authenticationToken,
      signal,
    );

  sendV8ScheduledRecordingDeleteRequest: (url: string, eTag: string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, '', eTag);

  sendV8ScheduledRecordingListRequest: (url: string, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, undefined, eTag);

  sendV8ScheduledRecordingMetadataRequest: (url: string, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, undefined, eTag);

  sendV8ScheduledRecordingUpdateRequest: (
    url: string,
    scheduledRecordId: string,
    fromUtc: ?string,
    toUtc: ?string,
    eTag: string,
    increasingEpisodes: ?boolean,
    recordsToKeep: ?number,
    method: HttpMethod,
    authenticationToken: string | null,
    signal?: AbortSignal,
  ) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (url, scheduledRecordId, fromUtc, toUtc, eTag, increasingEpisodes, recordsToKeep, method, authenticationToken, signal) =>
    sendV8ScheduledRecordingUpdateRequest(url, scheduledRecordId, fromUtc, toUtc, increasingEpisodes, recordsToKeep, method, eTag, authenticationToken, signal);

  sendV8SectionFeedRequest: (section: NETGEM_API_V8_SECTION, searchString: ?string, state: CombinedReducers, signal?: AbortSignal) => Promise<NETGEM_API_V8_FEED> = (
    section,
    searchString,
    state,
    signal,
  ) => sendV8SectionFeedRequest(section, searchString, state, signal);

  sendPersonalDataDeleteRequest: (url: string, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendV8GenericRequest(url, method, authenticationToken, null, signal, '', eTag);

  sendPersonalDataGetRequest: (url: string, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendPersonalDataGetRequest(url, eTag, method, authenticationToken, signal);

  sendPersonalDataPostRequest: (url: string, data: any, eTag: ?string, method: HttpMethod, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (
    url,
    data,
    eTag,
    method,
    authenticationToken,
    signal,
  ) => sendPersonalDataPostRequest(url, data, eTag, method, authenticationToken, signal);

  sendV8DataCollectionCollectorIdRequest: (url: string, authenticationToken: string | null, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (url, authenticationToken, signal) =>
    sendV8DataCollectionCollectorIdRequest(url, authenticationToken, signal);

  sendV8DataCollectionRequest: (
    url: string,
    content: NETGEM_API_V8_DATA_COLLECTION_BATCH_PAYLOAD,
    method: HttpMethod,
    headers: NETGEM_API_V8_REQUEST_HEADERS,
    signal?: AbortSignal,
  ) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (url, content, method, headers, signal) => sendV8DataCollectionRequest(url, content, method, headers, signal);

  sendV8CustomStrategyRequest: (url: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (url, signal) => sendV8GenericRequest(url, HttpMethod.GET, null, null, signal);

  sendVideofuturDiscoveryRequest: (discoveryUrl: string, device: string, distributor: string, signal?: AbortSignal) => Promise<any> = (discoveryUrl, device, distributor, signal) =>
    boDiscoveryRequest(discoveryUrl, device, distributor, signal);

  sendVideofuturRequest: (
    baseUrl: string,
    method: HttpMethod,
    authenticationToken: string | null,
    body: ?KeyValuePair<?(string | number | boolean)>,
    headers: ?NETGEM_API_V8_REQUEST_HEADERS,
    signal?: AbortSignal,
    keepAlive?: boolean,
    eTag?: string,
  ) => Promise<BO_RESPONSE_WITH_ETAG> = (baseUrl, method, authenticationToken, body, headers, signal, keepAlive, eTag) =>
    sendBORequest(baseUrl, method, authenticationToken, body, headers, signal, keepAlive, eTag);
}

export default NetgemApi;
